import type {
  ChangeOrderDocument,
  ChangeOrderReasonCode,
  ChangeOrderValidateRequestBody,
  ChangeOrderValidateResponse,
} from 'types';

import { api } from './api';

// Flat, full desired-state body (not a patch): system size/production required; productId optional
// (omit to keep the locked product) but at least one of productId or (kwhRate + escalationRate) must
// be sent; optional features follow present = included / absent = removed (battery omitted → removed;
// adders is the complete set).
export interface CreateChangeOrderBody {
  productId?: string;
  systemSizeKw: number;
  systemFirstYearProductionKwh: number;
  kwhRate?: number;
  escalationRate?: number;
  adders?: string[];
  // Full battery state; the rawCost dollar amount is nested as `cost` (matching v3 create-quote).
  battery?: object;
  reasons: ChangeOrderReasonCode[];
  confirmedByEpc?: boolean;
}

const changeOrdersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getChangeOrders: builder.query<ChangeOrderDocument[], string>({
      query: (accountId) => ({ url: `/v3/accounts/${accountId}/change-orders`, method: 'GET' }),
      providesTags: (_, __, accountId) => [{ type: 'ChangeOrders' as const, id: accountId }],
    }),
    validateChangeOrder: builder.query<
      ChangeOrderValidateResponse,
      { accountId: string; body?: ChangeOrderValidateRequestBody }
    >({
      query: ({ accountId, body }) => ({
        url: `/v3/accounts/${accountId}/change-orders/validate`,
        method: 'POST',
        body: body ?? {},
      }),
      providesTags: (_, __, { accountId }) => [{ type: 'ChangeOrders' as const, id: accountId }],
    }),
    createChangeOrder: builder.mutation<ChangeOrderDocument, { accountId: string; body: CreateChangeOrderBody }>({
      query: ({ accountId, body }) => ({
        url: `/v3/accounts/${accountId}/change-orders`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, __, { accountId }) => [
        { type: 'ChangeOrders' as const, id: accountId },
        { type: 'Quotes' as const, id: 'LIST' },
        { type: 'Accounts' as const, id: accountId },
        { type: 'Contracts' as const, id: 'LIST' },
        { type: 'AccountMilestones' as const, id: accountId },
      ],
    }),
    // Invalidates History so the resend shows up in the account activity timeline.
    resendChangeOrder: builder.mutation<{ sentCount: number }, { accountId: string; changeOrderId: string }>({
      query: ({ accountId, changeOrderId }) => ({
        url: `/v3/accounts/${accountId}/change-orders/${changeOrderId}/resend`,
        method: 'POST',
      }),
      invalidatesTags: (_, __, { accountId }) => [
        { type: 'ChangeOrders' as const, id: accountId },
        { type: 'History' as const, id: 'LIST' },
      ],
    }),
    cancelChangeOrder: builder.mutation<void, { accountId: string; changeOrderId: string }>({
      query: ({ accountId, changeOrderId }) => ({
        url: `/v3/accounts/${accountId}/change-orders/${changeOrderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { accountId }) => [
        { type: 'ChangeOrders' as const, id: accountId },
        { type: 'Quotes' as const, id: 'LIST' },
        { type: 'Accounts' as const, id: accountId },
        { type: 'Contracts' as const, id: 'LIST' },
        { type: 'AccountMilestones' as const, id: accountId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetChangeOrdersQuery,
  useValidateChangeOrderQuery,
  useCreateChangeOrderMutation,
  useCancelChangeOrderMutation,
  useResendChangeOrderMutation,
} = changeOrdersApi;
